import React, { useId, useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  PencilIcon,
  // EyeSlashIcon statt TrashIcon: der Endpunkt löscht nichts, er schaltet ab. Ein Papierkorb
  // hätte genau die Erwartung erzeugt, gegen die die ganze Taxonomie gebaut ist.
  EyeSlashIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { getErrorMessage } from '../utils/errors';
import {
  topicCategoryService,
  MainTopicCategoryData,
  SubTopicCategoryData
} from '../services/topicCategoryService';
import { PageLayout } from '../components/ui/PageLayout';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import type { BenachrichtigungsTyp } from '../components/ui/Benachrichtigung';
// Die Modal-Schicht stand bis hierher als lokale Konstante weiter unten in dieser Datei. Sie ist
// unverändert in den Baukasten gehoben, weil die Rückfrage-Abfrage denselben Schleier braucht —
// und ein zweiter, von Hand abgeschriebener Schleier wäre der Anfang derselben Drift wie beim
// Benachrichtigungs-Balken.
import { ModalSchicht } from '../components/ui/ModalSchicht';
import { Rueckfrage } from '../components/ui/Rueckfrage';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useRueckfrage } from '../hooks/useRueckfrage';

const TopicCategoryManagement: React.FC = () => {
  const [mainTopics, setMainTopics] = useState<MainTopicCategoryData[]>([]);
  const [subTopics, setSubTopics] = useState<SubTopicCategoryData[]>([]);
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [editingMainTopic, setEditingMainTopic] = useState<MainTopicCategoryData | null>(null);
  const [editingSubTopic, setEditingSubTopic] = useState<SubTopicCategoryData | null>(null);
  const [showMainTopicForm, setShowMainTopicForm] = useState(false);
  const [showSubTopicForm, setShowSubTopicForm] = useState(false);
  const [selectedMainTopicId, setSelectedMainTopicId] = useState<string | null>(null);
  // Zustand und Zeitmessung des Balkens liegen im Haken. Der frühere Effekt hier blendete BEIDE
  // Tonlagen nach 3000 ms aus — auch „Speichern fehlgeschlagen."; jetzt bleibt der Fehler stehen.
  const { inhalt, melde, schließe } = useBenachrichtigung();
  // ⚠️ Das Feld heißt `rückfrage`, nicht `inhalt` — der Name daneben ist schon vergeben. Der Haken
  // ist genau dafür so benannt (siehe `useRueckfrage.ts`).
  const { rückfrage, frage, antworte } = useRueckfrage();
  // Neu seit der API-Anbindung: Laden dauert und kann fehlschlagen.
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Seit die Taxonomie in der Datenbank liegt (PR #623), ist das ein Netzwerk-Aufruf und kann
  // fehlschlagen — vorher war es ein synchroner localStorage-Zugriff ohne Fehlerfall.
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // EIN Abruf für beide Ebenen: die Antwort trägt ohnehin den ganzen Baum. Zwei parallele
      // Aufrufe waren zwei identische HTTP-Abfragen für dieselben Daten.
      const { mainTopics: main, subTopics: sub } = await topicCategoryService.getTopics();
      setMainTopics(main);
      setSubTopics(sub);
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Themen konnten nicht geladen werden.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Steht bewusst NACH `loadData`: die memoisierte Funktion ist eine Abhängigkeit und wird
  // damit schon beim Rendern gelesen — davor wäre das die temporale Todeszone.
  useEffect(() => {
    void loadData();
  }, [loadData]);

  const toggleExpand = (mainTopicId: string) => {
    setExpandedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mainTopicId)) {
        newSet.delete(mainTopicId);
      } else {
        newSet.add(mainTopicId);
      }
      return newSet;
    });
  };

  const handleCreateMainTopic = () => {
    setEditingMainTopic(null);
    setShowMainTopicForm(true);
  };

  const handleEditMainTopic = (topic: MainTopicCategoryData) => {
    setEditingMainTopic(topic);
    setShowMainTopicForm(true);
  };

  // Deaktivieren, NICHT löschen: die Slugs stehen ohne Fremdschlüssel in
  // `content.metadata.topicCategories` — gelöscht würden Bestandsinhalte auf nichts zeigen.
  const handleDeactivateMainTopic = async (id: string) => {
    // Name und Zahl kommen aus dem Zustand, den die Zeile ohnehin anzeigt. Der native Dialog davor
    // konnte beides nicht nennen — er hatte eine einzige Zeichenkette, und die musste für jede der
    // Zeilen dieselbe sein.
    const name = mainTopics.find((t) => t.id === id)?.label;
    // ⚠️ ALLE Subthemen, nicht nur die aktiven: der Server fährt in EINER Transaktion
    // `topicCategory.update({ isActive: false })` plus
    // `topicSubcategory.updateMany({ where: { categoryId: id } })` — ohne Filter auf `isActive`
    // (nachgelesen in `TopicCategoryService.deactivateCategory`, nicht aus dem alten Meldungstext
    // übernommen). Dieselbe Zahl meldet danach der Erfolgs-Balken zurück.
    const subthemen = getSubTopicsForMainTopic(id).length;
    // ⚠️ `gefahr: true`, obwohl hier nachweislich NICHTS gelöscht wird — die Kaskade ist eine
    // Einbahnstraße. Zurück geht es über `PATCH { isActive: true }` auf dem Hauptthema, und das
    // rührt `topicSubcategory` nicht an (`TopicCategoryService.updateCategory` schreibt genau drei
    // Felder der eigenen Zeile). Welche Subthemen vorher aktiv waren, weiß danach niemand mehr:
    // das ist der Verlust, und er ist mit keinem Knopf zurückzuholen.
    const kaskade = (() => {
      if (subthemen === 0) return 'Subthemen hängen keine daran.';
      if (subthemen === 1) {
        return (
          'Mit abgeschaltet wird das eine Subthema darunter. Ein späteres Reaktivieren des ' +
          'Hauptthemas holt es NICHT zurück — es muss dann von Hand wieder aktiviert werden.'
        );
      }
      return (
        `Mit abgeschaltet werden alle ${subthemen} Subthemen darunter, in einem Zug. Ein späteres ` +
        'Reaktivieren des Hauptthemas holt sie NICHT zurück — jedes muss dann einzeln wieder ' +
        'aktiviert werden.'
      );
    })();
    const bestätigt = await frage({
      titel: 'Hauptthema deaktivieren?',
      text:
        `${name ? `„${name}"` : 'Dieses Hauptthema'} verschwindet aus der Themen-Auswahl. ` +
        'Bestehende Zuordnungen an Inhalten bleiben erhalten — gelöscht wird nichts.\n\n' +
        kaskade,
      // Pflicht-Angabe: die Vorgabe bei `gefahr` wäre „Löschen", und genau diese Erwartung
      // vermeidet diese Seite bis in die Symbolwahl hinein (`EyeSlashIcon` statt `TrashIcon`).
      bestätigenText: 'Deaktivieren',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      const count = await topicCategoryService.deactivateMainTopic(id);
      await loadData();
      melde('success', `Hauptthema deaktiviert (${count} Subthemen mit abgeschaltet).`);
    } catch (e) {
      melde('error', getErrorMessage(e, 'Deaktivieren fehlgeschlagen.'));
    }
  };

  const handleCreateSubTopic = (mainTopicId: string) => {
    setSelectedMainTopicId(mainTopicId);
    setEditingSubTopic(null);
    setShowSubTopicForm(true);
  };

  const handleEditSubTopic = (subTopic: SubTopicCategoryData) => {
    setEditingSubTopic(subTopic);
    setSelectedMainTopicId(subTopic.categoryId);
    setShowSubTopicForm(true);
  };

  const handleDeactivateSubTopic = async (id: string) => {
    const name = subTopics.find((s) => s.id === id)?.label;
    // ⚠️ Bewusst OHNE `gefahr` — anders als eine Zeile höher. Hier kippt genau ein `isActive` einer
    // einzigen Zeile (`deactivateSubTopic` ruft `updateSubTopic(id, { isActive: false })`), es hängt
    // nichts darunter, und der Umschalter im Bearbeiten-Formular macht es Feld für Feld rückgängig.
    // Rot wäre hier nicht „vorsichtiger", sondern teurer: sähe die harmlose Abfrage aus wie die
    // folgenreiche darüber, verlöre die Farbe ihre Aussage genau dort, wo sie gebraucht wird.
    const bestätigt = await frage({
      titel: 'Subthema deaktivieren?',
      text:
        `${name ? `„${name}"` : 'Dieses Subthema'} verschwindet aus der Themen-Auswahl. ` +
        'Bestehende Zuordnungen an Inhalten bleiben erhalten — gelöscht wird nichts.\n\n' +
        'Im Bearbeiten-Formular dieses Subthemas lässt es sich jederzeit wieder aktivieren.',
      bestätigenText: 'Deaktivieren',
    });
    if (!bestätigt) return;
    try {
      await topicCategoryService.deactivateSubTopic(id);
      await loadData();
      melde('success', 'Subthema deaktiviert.');
    } catch (e) {
      melde('error', getErrorMessage(e, 'Deaktivieren fehlgeschlagen.'));
    }
  };

  const getSubTopicsForMainTopic = (mainTopicId: string) => {
    return subTopics.filter(st => st.categoryId === mainTopicId).sort((a, b) => a.position - b.position);
  };

  return (
    <PageLayout
      title="Themen-Kategorien Verwaltung"
      subtitle="Verwalten Sie Hauptthemen und Subthemen für Ihre Inhalte"
      actions={
        <button
          onClick={handleCreateMainTopic}
          className="btn btn-primary"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Neues Hauptthema
        </button>
      }
    >
      {/* ⚠️ `schwebend` ist auf dieser Seite PFLICHT, nicht Geschmack: die beiden Formulare weiter
          unten sind Modals (`fixed inset-0 bg-black bg-opacity-50 z-50`), und ihr Fehlerzweig
          schließt das Modal absichtlich NICHT — `setShowMainTopicForm(false)` steht im `try`, nicht
          im `catch`, damit die Eingabe erhalten bleibt. Ein Balken im Seitenfluss läge damit hinter
          dem Overlay und außerhalb des Sichtfelds: die abgelehnte Speicherung (z. B. doppelter
          Schlüssel) wäre unsichtbar, das unveränderte Formular sähe aus, als sei der Klick ins
          Leere gegangen. Die Klassen und ihre Messwerte stehen an der Eigenschaft `schwebend` in
          `Benachrichtigung.tsx`. Der Balken ist derselbe geteilte Baustein wie auf den anderen elf
          Stellen — nur in seiner schwebenden Variante, keine Handkopie.
          Seit die Overlays per Portal an `document.body` hängen (siehe `ui/ModalSchicht.tsx`),
          stehen sie im Dokument sogar NACH dem Balken: bei Gleichstand entschiede die
          Dokumentreihenfolge gegen ihn. Das `z-[60]` gegen `z-50` ist damit nicht Kosmetik,
          sondern das Einzige, was ihn oben hält. */}
      <Benachrichtigung inhalt={inhalt} onSchließen={schließe} schwebend />

      {/* Die Rückfrage steht neben dem Balken, aus demselben Grund: eine Sicherheitsabfrage, die
          nur in einem Zustand der Seite im Baum steht, ist in den anderen keine.

          ⚠️ Nachgemessen statt von den Geschwisterseiten übernommen — diese Seite hat gar keine
          Formular-Weiche. Ihre beiden Formulare hängen als `{showMainTopicForm && …}` NEBEN der
          Liste, nicht statt ihrer; gefragt wird ausschließlich aus der Liste (die beiden
          `EyeSlashIcon`-Knöpfe in den Zeilen), die Modals selbst stellen keine Frage. Hier gibt es
          also nur diesen einen Ort.

          ⚠️ Die Stapelordnung ist auf DIESER Seite der heikle Teil, weil sie als einzige alle drei
          Schichten gleichzeitig zeigen kann: Modal `z-50`, Rückfrage `z-[55]`, Balken `z-[60]`.
          Die beiden Schleier hängen per Portal am selben `document.body` und stehen im Dokument
          NACH dem Balken — bei Gleichstand entschiede also die Reihenfolge, und der Balken verlöre.

          Am GEBAUTEN Stylesheet nachgemessen (`npm run build` + `vite preview`, Trefferprobe über
          `document.elementFromPoint`, Fenster 1280×720). Gemessene Werte: Modal 50, Rückfrage 55,
          Balken 60, Seitenleiste 50.
          • Bildmitte (640,360) mit allen drei Schichten → die Fläche der RÜCKFRAGE, nicht das
            Modal darunter.
          • Balkenmitte (1147,44) → der BALKEN, nicht der Schleier der Rückfrage.
          • über der Seitenleiste (100,360) → der Schleier der Rückfrage; er deckt also auch den
            zweiten `z-50`-Nachbarn.
          Kontrollpunkte, die etwas ANDERES treffen mussten — ohne sie belegte die Probe nur
          „irgendetwas ist da":
          • 400 px links vom Balken, gleiche Höhe → Schleier der Rückfrage, NICHT der Balken.
          • Rückfrage entfernt, dieselbe Mitte → Fläche des MODALS (die Mitte traf sie also nicht
            aus Geometrie-Zufall).
          • Modal zusätzlich entfernt, dieselbe Mitte → der Seiteninhalt.
          Dazu die Bedingung, unter der der Vergleich überhaupt gilt: auf dem Weg vom Balken zur
          Dokumentwurzel macht kein Vorfahre einen eigenen Stapelkontext auf (`transform`, `filter`,
          `opacity`, `will-change`, `isolation`, `contain`, `mix-blend-mode`, `backdrop-filter`,
          positioniertes `z-index` — alle geprüft, null Treffer). */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {/* Main Topics List */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-secondary-500">Themen werden geladen…</p>
            </div>
          ) : loadError ? (
            // Seit die Taxonomie über die API kommt, gibt es einen echten Fehlerfall — vorher
            // konnte ein localStorage-Zugriff nicht scheitern.
            <div className="p-6">
              <div className="bg-error-50 border border-error-200 rounded-md p-4">
                <p className="text-sm text-error-600">{loadError}</p>
              </div>
              <button onClick={() => void loadData()} className="btn btn-outline mt-4">
                Erneut versuchen
              </button>
            </div>
          ) : mainTopics.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-secondary-500">Keine Hauptthemen vorhanden</p>
            </div>
          ) : (
            <div className="divide-y divide-secondary-200">
              {[...mainTopics].sort((a, b) => a.position - b.position).map((mainTopic) => {
                const isExpanded = expandedTopics.has(mainTopic.id);
                const topicSubTopics = getSubTopicsForMainTopic(mainTopic.id);

                return (
                  <div key={mainTopic.id} className="p-4 hover:bg-secondary-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <button
                          onClick={() => toggleExpand(mainTopic.id)}
                          className="text-secondary-400 hover:text-secondary-600"
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="w-5 h-5" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5" />
                          )}
                        </button>
                        <div className="flex-1">
                          <h3
                            className={`text-lg font-semibold flex items-center ${
                              mainTopic.isActive ? 'text-secondary-900' : 'text-secondary-400'
                            }`}
                          >
                            {mainTopic.label}
                            {!mainTopic.isActive && (
                              <span className="ml-2 text-xs font-normal bg-warning-100 text-warning-800 px-2 py-0.5 rounded">
                                Deaktiviert
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-secondary-500">
                            Key: {mainTopic.key} • {topicSubTopics.length} Subthemen
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditMainTopic(mainTopic)}
                          className="p-2 text-secondary-400 hover:text-secondary-600"
                          title="Bearbeiten"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {/* Nur solange es etwas abzuschalten gibt. Zurück geht es über das
                            Bearbeiten-Formular — dort steht der Umschalter. */}
                        {mainTopic.isActive && (
                          <button
                            onClick={() => handleDeactivateMainTopic(mainTopic.id)}
                            className="p-2 text-warning-500 hover:text-warning-700"
                            title="Deaktivieren"
                          >
                            <EyeSlashIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleCreateSubTopic(mainTopic.id)}
                          className="btn btn-outline btn-sm"
                        >
                          <PlusIcon className="w-4 h-4 mr-1" />
                          Subthema
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 ml-8 space-y-2">
                        {topicSubTopics.length === 0 ? (
                          <p className="text-sm text-secondary-400 italic">Keine Subthemen vorhanden</p>
                        ) : (
                          topicSubTopics.map((subTopic) => (
                            <div
                              key={subTopic.id}
                              className="flex items-center justify-between p-3 card hover:border-secondary-300"
                            >
                              <div>
                                <p
                                  className={`text-sm font-medium flex items-center ${
                                    subTopic.isActive ? 'text-secondary-900' : 'text-secondary-400'
                                  }`}
                                >
                                  {subTopic.label}
                                  {!subTopic.isActive && (
                                    <span className="ml-2 text-xs font-normal bg-warning-100 text-warning-800 px-2 py-0.5 rounded">
                                      Deaktiviert
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-secondary-500">
                                  Key: {subTopic.key}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditSubTopic(subTopic)}
                                  className="p-1 text-secondary-400 hover:text-secondary-600"
                                  title="Bearbeiten"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                {subTopic.isActive && (
                                  <button
                                    onClick={() => handleDeactivateSubTopic(subTopic.id)}
                                    className="p-1 text-warning-500 hover:text-warning-700"
                                    title="Deaktivieren"
                                  >
                                    <EyeSlashIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main Topic Form Modal */}
      {showMainTopicForm && (
        <MainTopicForm
          topic={editingMainTopic}
          melde={melde}
          onSave={async (data) => {
            // ⚠️ `await` ist Pflicht: seit der API-Anbindung fängt ein synchrones try/catch die
            // abgelehnte Promise NICHT, und `loadData()` liefe vor dem Speichern.
            try {
              if (editingMainTopic) {
                // `key` bewusst NICHT mitschicken — der Slug steht in den Bestandsinhalten und
                // ist serverseitig ohnehin gesperrt.
                // `isActive` dagegen MUSS mit: der Deaktivieren-Knopf schaltet nur ab, das PATCH
                // hier ist der einzige Weg zurück.
                await topicCategoryService.updateMainTopic(editingMainTopic.id, {
                  label: data.label,
                  position: data.position,
                  isActive: data.isActive,
                });
                melde('success', 'Hauptthema aktualisiert.');
              } else {
                // `isActive` bleibt draußen — der Anlege-Endpunkt kennt das Feld nicht.
                await topicCategoryService.createMainTopic({
                  key: data.key,
                  label: data.label,
                  position: data.position,
                });
                melde('success', 'Hauptthema erstellt.');
              }
              await loadData();
              setShowMainTopicForm(false);
              setEditingMainTopic(null);
            } catch (e) {
              melde('error', getErrorMessage(e, 'Speichern fehlgeschlagen.'));
            }
          }}
          onCancel={() => {
            setShowMainTopicForm(false);
            setEditingMainTopic(null);
          }}
        />
      )}

      {/* Sub Topic Form Modal */}
      {showSubTopicForm && selectedMainTopicId && (
        <SubTopicForm
          subTopic={editingSubTopic}
          mainTopicId={selectedMainTopicId}
          mainTopicLabel={mainTopics.find(mt => mt.id === selectedMainTopicId)?.label || ''}
          melde={melde}
          onSave={async (data) => {
            try {
              if (editingSubTopic) {
                // `key` bleibt draußen — Slug ist unveränderlich (steht in den Bestandsinhalten).
                // `isActive` mit, aus demselben Grund wie beim Hauptthema: ein mit abgeschaltetes
                // Subthema bliebe sonst auch nach dem Reaktivieren seines Hauptthemas aus.
                await topicCategoryService.updateSubTopic(editingSubTopic.id, {
                  label: data.label,
                  position: data.position,
                  categoryId: data.categoryId,
                  isActive: data.isActive,
                });
                melde('success', 'Subthema aktualisiert.');
              } else {
                await topicCategoryService.createSubTopic({
                  key: data.key,
                  label: data.label,
                  categoryId: data.categoryId,
                  position: data.position,
                });
                melde('success', 'Subthema erstellt.');
              }
              await loadData();
              setShowSubTopicForm(false);
              setEditingSubTopic(null);
              setSelectedMainTopicId(null);
            } catch (e) {
              melde('error', getErrorMessage(e, 'Speichern fehlgeschlagen.'));
            }
          }}
          onCancel={() => {
            setShowSubTopicForm(false);
            setEditingSubTopic(null);
            setSelectedMainTopicId(null);
          }}
        />
      )}
    </PageLayout>
  );
};

/**
 * Formt eine Eingabe in einen Slug, den der Server akzeptiert.
 *
 * ⚠️ Vorher wurden nur Leerzeichen ersetzt. „Erfolg & Motivation" wurde damit zu
 * `erfolg-&-motivation`, und der Server antwortete 400 — der Hinweistext „wird automatisch in
 * URL-Format konvertiert" versprach also mehr, als er tat. Umlaute werden ausgeschrieben statt
 * entfernt, sonst würde aus „Ängste" ein `ngste`.
 */
const zuSlug = (eingabe: string): string =>
  eingabe
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

// Main Topic Form Component
interface MainTopicFormProps {
  topic: MainTopicCategoryData | null;
  onSave: (data: { key: string; label: string; position: number; isActive: boolean }) => void;
  onCancel: () => void;
  /** Meldeweg der Seite. Begründung am Restwächter in `handleSubmit`. */
  melde: (type: BenachrichtigungsTyp, message: string) => void;
}

const MainTopicForm: React.FC<MainTopicFormProps> = ({ topic, onSave, onCancel, melde }) => {
  // ⚠️ Erzeugte Wurzel statt fester Zeichenketten — hier besonders wichtig: dieses Formular und
  // `SubTopicForm` weiter unten führen dieselben Feldnamen („Label", „Key", „Reihenfolge"), und
  // beide hängen als Geschwister-Modale an derselben Seite. Feste Kennungen wären doppelt, sobald
  // beide offen stehen, und `htmlFor` träfe das Feld des jeweils anderen Formulars.
  const feldId = useId();
  const istBearbeitung = topic !== null;
  const [formData, setFormData] = useState({
    key: topic?.key || '',
    label: topic?.label || '',
    position: topic?.position || 0,
    isActive: topic?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // PFLICHTFELDER — der Browser sagt es selbst, am Feld.
    //
    // Hier stand `alert('Bitte füllen Sie alle Pflichtfelder aus.')`. „Label" und „Key" tragen
    // beide `required`; ein leeres Pflichtfeld blockt das Absenden, bevor diese Funktion überhaupt
    // läuft — nachgemessen unter jsdom: 0 Aufrufe. Für den Normalfall braucht es hier also gar
    // keine Meldung, und die Rückmeldung steht ohne Zutun an der richtigen Stelle.
    //
    // ⚠️ WARUM der Wächter trotzdem stehen bleibt, statt ersatzlos zu verschwinden: „Key" ist beim
    // BEARBEITEN `disabled`, und ein gesperrtes Feld ist von der Prüfung des Browsers ausgenommen —
    // ebenfalls nachgemessen (1 Aufruf trotz leerem `required`). Käme ein Hauptthema ohne Schlüssel
    // aus der Schnittstelle, liefe es genau hier durch und würde still gespeichert. Diesen Rest
    // deckt der Browser prinzipiell nicht ab.
    //
    // Gemeldet wird über den Balken der Seite, nicht als Hinweis am Feld: das gesperrte Feld kann
    // die Nutzerin nicht ausfüllen: ein Hinweis darunter forderte etwas Unmögliches. Der Balken
    // steht auf dieser Seite `schwebend` und liegt damit über dem Schleier dieses Modals.
    if (!formData.key || !formData.label) {
      const fehlend = [!formData.label && 'Label', !formData.key && 'Key'].filter(Boolean).join(' und ');
      melde('error', `Pflichtfeld fehlt: ${fehlend}. Speichern nicht möglich.`);
      return;
    }
    onSave(formData);
  };

  return (
    <ModalSchicht>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-secondary-900 mb-4">
          {topic ? 'Hauptthema bearbeiten' : 'Neues Hauptthema erstellen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor={`${feldId}-label`} className="block text-sm font-medium text-secondary-700 mb-2">
              Label *
            </label>
            <input
              id={`${feldId}-label`}
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="input w-full"
              placeholder="z.B. Erfolg & Motivation"
              required
            />
          </div>
          <div>
            <label htmlFor={`${feldId}-key`} className="block text-sm font-medium text-secondary-700 mb-2">
              Key (URL-freundlich) *
            </label>
            {/* Beim Bearbeiten gesperrt: der Server übernimmt `key` bewusst NICHT aus dem
                Body — er steht als roher String in `content.metadata.topicCategories` der
                Bestandsinhalte. Ein tippbares Feld wäre ein stiller Blindgänger. */}
            <input
              id={`${feldId}-key`}
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: zuSlug(e.target.value) })}
              className="input w-full disabled:bg-secondary-100 disabled:text-secondary-500 disabled:cursor-not-allowed"
              placeholder="z.B. erfolg-motivation"
              disabled={istBearbeitung}
              required
            />
            <p className="text-xs text-secondary-500 mt-1">
              {istBearbeitung
                ? 'Nicht änderbar: der Schlüssel steht in bereits zugeordneten Inhalten.'
                : 'Wird automatisch in URL-Format konvertiert'}
            </p>
          </div>
          <div>
            <label htmlFor={`${feldId}-position`} className="block text-sm font-medium text-secondary-700 mb-2">
              Reihenfolge
            </label>
            <input
              id={`${feldId}-position`}
              type="number"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 0 })}
              max={100000}
              className="input w-full"
              min="0"
            />
          </div>
          {/* Nur beim Bearbeiten: der Anlege-Endpunkt kennt kein `isActive`, Neuanlagen sind
              immer aktiv. Ein Umschalter im Erstellen-Dialog verspräche etwas, das nicht ankommt. */}
          {topic && (
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={`${feldId}-aktiv`}
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <label htmlFor={`${feldId}-aktiv`} className="ml-2 block text-sm text-secondary-700">
                  Aktiv (in der Themen-Auswahl wählbar)
                </label>
              </div>
              <p className="text-xs text-secondary-500 mt-1">
                Deaktiviert verschwindet das Thema aus der Auswahl; bestehende Zuordnungen bleiben erhalten.
                Subthemen behalten ihren eigenen Status — nach dem Reaktivieren ggf. einzeln nachziehen.
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {topic ? 'Aktualisieren' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </ModalSchicht>
  );
};

// Sub Topic Form Component
interface SubTopicFormProps {
  subTopic: SubTopicCategoryData | null;
  mainTopicId: string;
  mainTopicLabel: string;
  onSave: (data: {
    key: string;
    label: string;
    categoryId: string;
    position: number;
    isActive: boolean;
  }) => void;
  onCancel: () => void;
  /** Meldeweg der Seite — wie beim Hauptthema, Begründung dort. */
  melde: (type: BenachrichtigungsTyp, message: string) => void;
}

const SubTopicForm: React.FC<SubTopicFormProps> = ({ subTopic, mainTopicId, mainTopicLabel, onSave, onCancel, melde }) => {
  // Eigene Wurzel je Instanz — Begründung bei `MainTopicForm`.
  const feldId = useId();
  const istBearbeitung = subTopic !== null;
  const [formData, setFormData] = useState({
    key: subTopic?.key || '',
    label: subTopic?.label || '',
    categoryId: subTopic?.categoryId || mainTopicId,
    position: subTopic?.position || 0,
    isActive: subTopic?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Wortgleich zum Hauptthema-Formular darüber, aus demselben Grund — dort ausführlich
    // begründet: `required` an beiden Feldern deckt den Normalfall am Feld ab, der Wächter hier
    // deckt nur den Rest, den der Browser prinzipiell nicht sieht (gesperrtes „Key" beim
    // Bearbeiten).
    if (!formData.key || !formData.label) {
      const fehlend = [!formData.label && 'Label', !formData.key && 'Key'].filter(Boolean).join(' und ');
      melde('error', `Pflichtfeld fehlt: ${fehlend}. Speichern nicht möglich.`);
      return;
    }
    onSave(formData);
  };

  return (
    <ModalSchicht>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-secondary-900 mb-4">
          {subTopic ? 'Subthema bearbeiten' : 'Neues Subthema erstellen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor={`${feldId}-hauptthema`} className="block text-sm font-medium text-secondary-700 mb-2">
              Hauptthema
            </label>
            <input
              id={`${feldId}-hauptthema`}
              type="text"
              value={mainTopicLabel}
              className="input w-full disabled:bg-secondary-100 disabled:text-secondary-500 disabled:cursor-not-allowed"
              disabled
            />
          </div>
          <div>
            <label htmlFor={`${feldId}-label`} className="block text-sm font-medium text-secondary-700 mb-2">
              Label *
            </label>
            <input
              id={`${feldId}-label`}
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="input w-full"
              placeholder="z.B. Beruflicher Erfolg"
              required
            />
          </div>
          <div>
            <label htmlFor={`${feldId}-key`} className="block text-sm font-medium text-secondary-700 mb-2">
              Key (URL-freundlich) *
            </label>
            {/* Beim Bearbeiten gesperrt: der Server übernimmt `key` bewusst NICHT aus dem
                Body — er steht als roher String in `content.metadata.topicCategories` der
                Bestandsinhalte. Ein tippbares Feld wäre ein stiller Blindgänger. */}
            <input
              id={`${feldId}-key`}
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: zuSlug(e.target.value) })}
              className="input w-full disabled:bg-secondary-100 disabled:text-secondary-500 disabled:cursor-not-allowed"
              placeholder="z.B. beruflicher-erfolg"
              disabled={istBearbeitung}
              required
            />
            <p className="text-xs text-secondary-500 mt-1">
              {istBearbeitung
                ? 'Nicht änderbar: der Schlüssel steht in bereits zugeordneten Inhalten.'
                : 'Wird automatisch in URL-Format konvertiert'}
            </p>
          </div>
          <div>
            <label htmlFor={`${feldId}-position`} className="block text-sm font-medium text-secondary-700 mb-2">
              Reihenfolge
            </label>
            <input
              id={`${feldId}-position`}
              type="number"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 0 })}
              max={100000}
              className="input w-full"
              min="0"
            />
          </div>
          {/* Wie beim Hauptthema: nur beim Bearbeiten, der Anlege-Endpunkt kennt kein `isActive`. */}
          {subTopic && (
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={`${feldId}-aktiv`}
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <label htmlFor={`${feldId}-aktiv`} className="ml-2 block text-sm text-secondary-700">
                  Aktiv (in der Themen-Auswahl wählbar)
                </label>
              </div>
              <p className="text-xs text-secondary-500 mt-1">
                Wählbar bleibt ein Subthema nur, solange auch sein Hauptthema aktiv ist.
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {subTopic ? 'Aktualisieren' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </ModalSchicht>
  );
};

export default TopicCategoryManagement;
